import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import PropTypes from 'prop-types';
import { IconPlus, IconEdit, IconTrash } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const emptyValue = (column) => {
    if (column.type === 'checkbox') return column.default ?? true;
    if (column.type === 'number') return column.default ?? 0;
    return column.default ?? '';
};

/**
 * CRUD genérico pros cadastros de referência da Etapa 1 (Estruturas
 * auxiliares) que têm o mesmo formato - lista + form simples (Unidade de
 * Medida, Moeda, Banco, Forma de Pagamento, Condição de Pagamento,
 * Centro de Custo, Categoria Financeira). Evita reescrever a mesma
 * tela 7 vezes; os que precisam de algo mais específico (Geografia,
 * Contas Bancárias) têm tela própria.
 */
const SimpleLookupCrud = ({ icon, gradient, title, subtitle, endpoint, columns, addLabel }) => {
    const [appId, setAppId] = useState(null);
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form, setForm] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadItems = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/${endpoint}/`);
            setItems(res.data || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar lista' });
        }
    };

    const selfOptions = useMemo(
        () => items.filter((item) => !editingItem || item.id !== editingItem.id),
        [items, editingItem]
    );

    const optionsFor = (column) => (column.optionsSource === 'self' ? selfOptions : column.options || []);

    const handleOpenCreate = () => {
        setEditingItem(null);
        const initial = {};
        columns.forEach((column) => {
            initial[column.key] = emptyValue(column);
        });
        setForm(initial);
        setDialogOpen(true);
    };

    const handleOpenEdit = (item) => {
        setEditingItem(item);
        const initial = {};
        columns.forEach((column) => {
            initial[column.key] = item[column.key] ?? emptyValue(column);
        });
        setForm(initial);
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editingItem) {
                await api.patch(`v1/apps/${appId}/${endpoint}/${editingItem.id}/`, form);
            } else {
                await api.post(`v1/apps/${appId}/${endpoint}/`, form);
            }
            setDialogOpen(false);
            loadItems();
        } catch (error) {
            console.error(error);
            setStatus({
                type: 'error',
                message: error.response?.data?.detail || Object.values(error.response?.data || {})[0] || 'Erro ao salvar'
            });
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Excluir "${item[columns[0].key]}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/${endpoint}/${item.id}/`);
            setItems((prev) => prev.filter((i) => i.id !== item.id));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir' });
        }
    };

    return (
        <Box>
            <PageHeader
                icon={icon}
                gradient={gradient}
                title={title}
                subtitle={subtitle}
                actions={
                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleOpenCreate}>
                        {addLabel || 'Adicionar'}
                    </Button>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell key={column.key}>{column.label}</TableCell>
                                ))}
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow key={item.id} hover>
                                    {columns.map((column) => (
                                        <TableCell key={column.key}>
                                            {column.type === 'checkbox'
                                                ? item[column.key]
                                                    ? 'Sim'
                                                    : 'Não'
                                                : column.type === 'select'
                                                  ? column.optionsSource === 'self'
                                                      ? items.find((i) => i.id === item[column.key])?.[columns[0].key] || '—'
                                                      : optionsFor(column).find((o) => o.value === item[column.key])?.label ||
                                                        item[column.key] ||
                                                        '—'
                                                  : item[column.key] ?? '—'}
                                        </TableCell>
                                    ))}
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleOpenEdit(item)}>
                                            <IconEdit size={16} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(item)}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={columns.length + 1} align="center">
                                        Nenhum registro cadastrado ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingItem ? 'Editar' : addLabel || 'Adicionar'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {columns.map((column) =>
                            column.type === 'checkbox' ? (
                                <FormControlLabel
                                    key={column.key}
                                    control={
                                        <Checkbox
                                            checked={!!form[column.key]}
                                            onChange={(e) => setForm({ ...form, [column.key]: e.target.checked })}
                                        />
                                    }
                                    label={column.label}
                                />
                            ) : column.type === 'select' ? (
                                <TextField
                                    key={column.key}
                                    select
                                    fullWidth
                                    label={column.label}
                                    value={form[column.key] ?? ''}
                                    onChange={(e) => setForm({ ...form, [column.key]: e.target.value })}
                                >
                                    <MenuItem value="">Nenhum</MenuItem>
                                    {optionsFor(column).map((option) =>
                                        column.optionsSource === 'self' ? (
                                            <MenuItem key={option.id} value={option.id}>
                                                {option[columns[0].key]}
                                            </MenuItem>
                                        ) : (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        )
                                    )}
                                </TextField>
                            ) : (
                                <TextField
                                    key={column.key}
                                    fullWidth
                                    type={column.type === 'number' ? 'number' : 'text'}
                                    label={column.label}
                                    required={column.required}
                                    value={form[column.key] ?? ''}
                                    onChange={(e) => setForm({ ...form, [column.key]: e.target.value })}
                                />
                            )
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

SimpleLookupCrud.propTypes = {
    icon: PropTypes.elementType,
    gradient: PropTypes.string,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    endpoint: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            type: PropTypes.oneOf(['text', 'number', 'checkbox', 'select']),
            required: PropTypes.bool,
            optionsSource: PropTypes.oneOf(['self']),
            options: PropTypes.array,
            default: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
        })
    ).isRequired,
    addLabel: PropTypes.string
};

export default SimpleLookupCrud;
